import { useEffect, useState } from 'react';
import axios from 'axios';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, Mail, Phone, Calendar, CheckCircle, X } from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminLeadsPage = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/leads`);
      setLeads(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching leads:', err);
      setError('Failed to load leads');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <Loader2 className="h-12 w-12 animate-spin text-[#10B981]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-error">
        <div className="text-center">
          <X size={48} className="text-red-500 mx-auto mb-4" />
          <p className="text-xl text-slate-900">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12" data-testid="admin-leads-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-8">
          <h1 className="font-heading text-4xl font-bold text-slate-900 mb-2" data-testid="admin-heading">
            Lead Management
          </h1>
          <p className="text-slate-600" data-testid="admin-description">
            Total Leads: <strong>{leads.length}</strong>
          </p>
        </div>

        {leads.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center" data-testid="admin-no-leads">
            <Mail size={48} className="text-slate-300 mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">No leads yet</h2>
            <p className="text-slate-600">Leads will appear here once customers submit the contact form.</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm" data-testid="admin-leads-table">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead data-testid="table-header-date">Date</TableHead>
                    <TableHead data-testid="table-header-name">Name</TableHead>
                    <TableHead data-testid="table-header-email">Email</TableHead>
                    <TableHead data-testid="table-header-phone">Phone</TableHead>
                    <TableHead data-testid="table-header-business">Business</TableHead>
                    <TableHead data-testid="table-header-project">Project Type</TableHead>
                    <TableHead data-testid="table-header-budget">Budget</TableHead>
                    <TableHead data-testid="table-header-timeline">Timeline</TableHead>
                    <TableHead data-testid="table-header-audit">Free Audit</TableHead>
                    <TableHead data-testid="table-header-message">Message</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.map((lead, index) => (
                    <TableRow key={lead.id} data-testid={`lead-row-${index}`}>
                      <TableCell data-testid={`lead-date-${index}`}>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar size={16} className="text-slate-400" />
                          {format(new Date(lead.created_at), 'MMM dd, yyyy')}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium" data-testid={`lead-name-${index}`}>
                        {lead.name}
                      </TableCell>
                      <TableCell data-testid={`lead-email-${index}`}>
                        <a href={`mailto:${lead.email}`} className="text-[#10B981] hover:underline flex items-center gap-1">
                          <Mail size={14} />
                          {lead.email}
                        </a>
                      </TableCell>
                      <TableCell data-testid={`lead-phone-${index}`}>
                        {lead.phone ? (
                          <a href={`tel:${lead.phone}`} className="flex items-center gap-1 text-slate-700 hover:text-[#10B981]">
                            <Phone size={14} />
                            {lead.phone}
                          </a>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell data-testid={`lead-business-${index}`}>
                        {lead.business_name || <span className="text-slate-400">-</span>}
                      </TableCell>
                      <TableCell data-testid={`lead-project-${index}`}>
                        <span className="bg-slate-100 px-2 py-1 rounded text-xs font-mono">
                          {lead.project_type}
                        </span>
                      </TableCell>
                      <TableCell data-testid={`lead-budget-${index}`}>
                        <span className="text-sm font-medium">{lead.budget_range}</span>
                      </TableCell>
                      <TableCell data-testid={`lead-timeline-${index}`}>
                        <span className="text-sm">{lead.timeline}</span>
                      </TableCell>
                      <TableCell data-testid={`lead-audit-${index}`}>
                        {lead.free_audit ? (
                          <CheckCircle size={20} className="text-[#10B981]" />
                        ) : (
                          <X size={20} className="text-slate-300" />
                        )}
                      </TableCell>
                      <TableCell className="max-w-xs" data-testid={`lead-message-${index}`}>
                        <p className="text-sm text-slate-600 truncate" title={lead.message}>
                          {lead.message}
                        </p>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
